import React, { useEffect, useState } from "react";
import { adminCreateProduct, adminDeleteProduct, adminUpdateProduct, getProducts } from "../services/api";
import AdminLayout from "./AdminLayout";
import { getImageUrl } from "../utils/imageHelper";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (err) {
      console.error("Failed to load products", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setName("");
    setPrice("");
    setStock("");
    setDescription("");
    setImage(null);
    setImagePreview(null);
    setIsEditing(false);
    setEditId(null);
  };

  // Function to Crop and Compress image on client side (More aggressive)
  const processImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const size = 600; // Target Size 600x600 for even smaller size
          canvas.width = size;
          canvas.height = size;
          
          const ctx = canvas.getContext('2d');
          
          let width = img.width;
          let height = img.height;
          let offsetX = 0;
          let offsetY = 0;
          
          if (width > height) {
            offsetX = (width - height) / 2;
            width = height;
          } else {
            offsetY = (height - width) / 2;
            height = width;
          }
          
          ctx.drawImage(img, offsetX, offsetY, width, height, 0, 0, size, size);
          
          canvas.toBlob((blob) => {
            const processedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve({
              blob: processedFile,
              preview: canvas.toDataURL('image/jpeg', 0.6) // Compressed quality 0.6
            });
          }, 'image/jpeg', 0.6);
        };
      };
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoading(true);
      try {
        const processed = await processImage(file);
        setImage(processed.blob);
        setImagePreview(processed.preview);
      } catch (err) {
        alert("Gagal memproses gambar. Coba gambar lain.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("price", price);
      fd.append("stock", stock);
      fd.append("description", description || "");
      
      if (image) {
        fd.append("image", image);
      }

      if (isEditing) {
        fd.append("_method", "POST"); 
        await adminUpdateProduct(editId, fd);
        alert("Produk berhasil diperbarui ✨");
      } else {
        await adminCreateProduct(fd);
        alert("Produk berhasil ditambahkan ✨");
      }
      
      resetForm();
      await load();
    } catch (err) {
      console.error("DEBUG ERROR:", err.response?.data);
      const serverMsg = err.response?.data?.message || err.response?.data?.errors?.image?.[0];
      alert(serverMsg ? `Gagal: ${serverMsg}` : "Terjadi kesalahan koneksi atau ukuran file terlalu besar.");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (p) => {
    setName(p.name);
    setPrice(p.price);
    setStock(p.stock);
    setDescription(p.description || "");
    setIsEditing(true);
    setEditId(p.id);
    setImagePreview(getImageUrl(p.image, null));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm("Hapus produk ini secara permanen?")) return;
    try {
      await adminDeleteProduct(id);
      await load();
    } catch (err) {
      alert("Gagal menghapus produk.");
    }
  };

  return (
    <AdminLayout>
      <div className="section-header" style={{ textAlign: 'left', margin: '0 0 30px' }}>
        <h2 className="section-title">Kelola Inventaris Produk</h2>
      </div>

      <div className="auth-card" style={{ maxWidth: '900px', margin: '0 0 40px' }}>
        <h3 style={{ marginBottom: '25px', color: 'var(--primary)' }}>
          {isEditing ? "📝 Edit Detail Produk" : "🆕 Tambah Koleksi Baru"}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="admin-product-form-grid">
            {/* Image Upload Area */}
            <div style={{ position: 'relative' }}>
               <div style={{ 
                 width: '100%', 
                 aspectRatio: '1/1', 
                 background: '#f8f8f8', 
                 borderRadius: '20px', 
                 border: '2px dashed var(--border)',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 overflow: 'hidden',
                 cursor: 'pointer'
               }} onClick={() => document.getElementById('fileInput').click()}>
                 {imagePreview ? (
                   <img src={imagePreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Preview" />
                 ) : (
                   <div style={{ textAlign: 'center', color: '#aaa' }}>
                     <span style={{ fontSize: '3rem' }}>📸</span>
                     <p style={{ fontSize: '0.8rem', marginTop: '10px' }}>Klik untuk upload<br/>(Otomatis Crop & Kompres)</p>
                   </div>
                 )}
               </div>
               <input id="fileInput" type="file" hidden accept="image/*" onChange={handleImageChange} />
            </div>

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div className="admin-form-grid-2" style={{ gap: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Nama Produk</label>
                  <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Contoh: Tas Rotan Ubud" />
                </div>
                <div className="form-group">
                  <label className="form-label">Stok</label>
                  <input className="form-input" type="number" value={stock} onChange={(e) => setStock(e.target.value)} required placeholder="0" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Harga (Rp)</label>
                <input className="form-input" type="number" value={price} onChange={(e) => setPrice(e.target.value)} required placeholder="150000" />
              </div>

              <div className="form-group">
                <label className="form-label">Deskripsi Produk</label>
                <textarea className="form-input" rows="4" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tuliskan spesifikasi dan keunggulan produk ini..." />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button type="submit" className="btn btn-primary" disabled={loading} style={{ flex: 1 }}>
                  {loading ? "⌛ Memproses..." : (isEditing ? "Simpan Perbaikan" : "Publikasikan Produk")}
                </button>
                {isEditing && (
                  <button type="button" className="btn btn-outline" onClick={resetForm}>
                    Batalkan
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
         <h3 style={{ margin: 0 }}>Daftar Koleksi Anda ({products.length})</h3>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Visual</th>
            <th>Detail Item</th>
            <th>Harga</th>
            <th>Stok</th>
            <th>Kelola</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.id}>
              <td style={{ width: '100px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <img 
                    src={getImageUrl(p.image, 'https://via.placeholder.com/80?text=No+Image')} 
                    alt={p.name} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </td>
              <td>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--secondary)' }}>{p.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '5px' }}>{p.description?.substring(0, 60)}...</div>
              </td>
              <td style={{ fontWeight: 700 }}>Rp {parseFloat(p.price).toLocaleString()}</td>
              <td>
                <span style={{ 
                  padding: '4px 10px', 
                  background: p.stock > 5 ? '#E8F5E9' : '#FFF3E0', 
                  color: p.stock > 5 ? '#2E7D32' : '#E65100',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}>
                  {p.stock} Unit
                </span>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button 
                  onClick={() => startEdit(p)} 
                  style={{ color: 'var(--primary)', fontWeight: 600, padding: '8px', marginRight: '10px' }}
                >
                  Ubah
                </button>
                <button 
                  onClick={() => del(p.id)} 
                  style={{ color: '#d32f2f', fontWeight: 600, padding: '8px' }}
                >
                  Hapus
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </AdminLayout>
  );
}
